import { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Stack, styled, Typography } from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';

import Button from '@components/Button';
import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { RefetchingIndicator } from '@components/RefetchingIndicator';
import { useAdminPanelStore } from '@store/adminPanelStore';
import { CountryList, FilesUploadResponse } from '@services/types';

import { regionalKnowledgeFilesTexts } from '../../constants';
import { useGeneralKnowledgeFiles } from '../../services/queries';
import {
    FilesDataToUpdate,
    FileUploadConstraints,
    GeneralKnowledgeFiles,
} from '../../services/types';
import { MenuOptionValue } from '../../types';

import { DocumentsList } from './components/DocumentsList';
import { MemoizedIndexerStatus } from './components/IndexerStatus';
import { SelectedFilesPreview } from './components/SelectedFilesPreview';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    width: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
});

interface FilesFormProps {
    filesByCountryData: GeneralKnowledgeFiles;
    mutate: UseMutateFunction<FilesUploadResponse, Error, FilesDataToUpdate>;
    countryList: CountryList;
    fileUploadConstraints: FileUploadConstraints;
    isSending: boolean;
}

type RegionalKnowledgeFilesFormData = Record<string, FileList | null>;

const FilesForm = ({
    filesByCountryData,
    countryList,
    mutate,
    fileUploadConstraints,
    isSending,
}: FilesFormProps) => {
    const setFilesUploadResponse = useAdminPanelStore((state) => state.setFilesUploadResponse);
    const clearFilesUploadResponse = useAdminPanelStore((state) => state.clearFilesUploadResponse);
    const defaultValues = countryList.reduce<RegionalKnowledgeFilesFormData>((acc, { value }) => {
        acc[value] = null;
        return acc;
    }, {});

    const { control, handleSubmit, watch, resetField, reset } =
        useForm<RegionalKnowledgeFilesFormData>({
            defaultValues,
        });

    const onSubmit = (updatedFilesData: RegionalKnowledgeFilesFormData) => {
        const filesToUpdate: FilesDataToUpdate = Object.entries(
            updatedFilesData
        ).reduce<FilesDataToUpdate>((acc, [country, fileList]) => {
            if (fileList && fileList.length > 0) {
                const files = Array.from(fileList).map((file) => ({
                    country,
                    file,
                }));
                acc.push(...files);
            }
            return acc;
        }, []);

        if (filesToUpdate.length > 0) {
            mutate(filesToUpdate, {
                //we use this to handle partial success (some files failed to upload, but not all of them)
                onSuccess: (data) => {
                    setFilesUploadResponse(data);
                },
                onError: () => {
                    clearFilesUploadResponse();
                },
                onSettled: () => {
                    reset(defaultValues);
                },
            });
        }
    };

    const countryFields = countryList
        .map(({ value, isCountry }) => ({
            country: value,
            isCountry,
            documents:
                filesByCountryData.find(({ country }) => country === value)?.documents || null,
        }))
        .sort((a, b) => {
            if (a.isCountry && !b.isCountry) return 1;
            if (!a.isCountry && b.isCountry) return -1;

            const aHasDocuments = a.documents && a.documents.length > 0;
            const bHasDocuments = b.documents && b.documents.length > 0;

            if (aHasDocuments && !bHasDocuments) return -1;
            if (!aHasDocuments && bHasDocuments) return 1;

            return a.country.localeCompare(b.country);
        });

    return (
        <Stack
            component='form'
            gap={3}
            id={MenuOptionValue.RegionalKnowledgeFiles}
            onSubmit={handleSubmit(onSubmit)}
        >
            {countryFields.map(({ country, documents }) => (
                <Fragment key={country}>
                    <Stack direction='row' alignItems='center' gap={4}>
                        <Typography variant='body1'>{country}</Typography>
                        <Controller
                            name={country}
                            control={control}
                            render={({ field: { onChange } }) => (
                                <Button
                                    lighterFont
                                    component='label'
                                    tabIndex={-1}
                                    disabled={isSending}
                                >
                                    {regionalKnowledgeFilesTexts.uploadFilesLabel}
                                    <VisuallyHiddenInput
                                        type='file'
                                        accept={fileUploadConstraints.permittedExtensions.join(
                                            ', '
                                        )}
                                        multiple
                                        onChange={(event) => onChange(event.target.files)}
                                    />
                                </Button>
                            )}
                        />
                    </Stack>

                    <SelectedFilesPreview
                        selectedFiles={watch(country)}
                        resetField={() => resetField(country)}
                    />
                    <DocumentsList documents={documents} />
                </Fragment>
            ))}
        </Stack>
    );
};

interface RegionalKnowledgeFilesProps {
    mutate: UseMutateFunction<FilesUploadResponse, Error, FilesDataToUpdate>;
    isSending: boolean;
}

export const RegionalKnowledgeFiles = ({ mutate, isSending }: RegionalKnowledgeFilesProps) => {
    const {
        data: { generalKnowledgeFiles, fileUploadConstraints, countryList },
        error,
        isError,
        isLoading,
        isFetching,
    } = useGeneralKnowledgeFiles();

    const getContent = () => {
        if (isLoading) return <Loader />;

        if (isError) return <ErrorMessage error={error} />;

        const missingDataMessages = [];

        if (!fileUploadConstraints) {
            missingDataMessages.push(regionalKnowledgeFilesTexts.noFileUploadConstraintsFound);
        }
        if (!countryList || countryList.length === 0) {
            missingDataMessages.push(regionalKnowledgeFilesTexts.noSupportedCountriesFound);
        }

        const hasMissingDataMessage = missingDataMessages.length > 0;

        if (hasMissingDataMessage)
            return <Typography variant='body2'>{missingDataMessages.join('\u0020')}</Typography>;

        if (!generalKnowledgeFiles || !fileUploadConstraints || !countryList) return null;

        const isRefetching = !isLoading && isFetching;

        return (
            <>
                {isRefetching && <RefetchingIndicator />}
                <FilesForm
                    filesByCountryData={generalKnowledgeFiles}
                    countryList={countryList}
                    fileUploadConstraints={fileUploadConstraints}
                    mutate={mutate}
                    isSending={isSending}
                />
            </>
        );
    };

    return (
        <>
            <MemoizedIndexerStatus />
            <Box mt={3}>{getContent()}</Box>
        </>
    );
};
